import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { humanize, unescapeHTML, titleize } from 'underscore.string';
import { Link } from 'react-router-dom';
import SessionStore from 'stores/SessionStore';
import moment from 'moment-timezone';
import StateTransition from 'components/v2/state-transition';
import { ProgressCell } from 'components/modules/shipments/renderer';
import { Menu } from '@headlessui/react';
import Badge from 'components/v2/Badge';
import DropdownMenu from 'components/v2/dropdowns/DropdownMenu';
import DropdownItem from 'components/v2/dropdowns/DropdownItem';
import { sluggify } from 'utils/localization/utils';
import { checkShorthaulAppointmentNeeded, isShipmentCanceled, } from 'helpers/shipments';
import { Glyphicon, OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import ShipmentDetailDateItem from 'components/modules/shipments/details/ShipmentDetailDateItem';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import { isProd } from 'helpers/helper_functions';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import Switch from 'react-switch';
import { getLocationTimezone } from 'helpers/locations';
import { getFirstStop, getLastStop } from 'helpers/stops';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import { withTranslation, Translation, Trans } from 'react-i18next';
import Alert from 'components/ui/Alert';
import BillingStatusRenderer from './BillingStatusRenderer';
import { LegNumberSummary } from './LegNumberSummary/LegNumberSummary';
import OpenExceptions from './OpenExceptions';
import DescriptionListItem from '../../v2/DescriptionListItem';
import InfoIcon from '../../v2/icons/InfoIcon';
import XIcon from '../../v2/icons/XIcon';
import NextSteps from './next-steps/NextSteps';
import MinimumDocumentRequirements from './MinimumDocumentRequirements';
// Removed unused imports SHIPMENT_STATUS_MAPPING, SHORTHAUL_SHIPMENT_GROUP
import { isOrderHazmat } from 'helpers/order-types';
import { Button } from '@cargomatic/ui';
const closedStatuses = ['canceled', 'complete'];
const shipmentTypes = {
    SHORTHAUL: 'shorthaul',
    LONGHAUL: 'longhaul',
    DAYRATE: 'dayrate',
};
const DISABLED_BOL_STATUSES = ['job_canceled', 'job_closed'];
function getBadgeColor(status) {
    if (!closedStatuses.includes(status)) {
        return 'brand';
    }
    return '';
}
const TOOLTIPS = {
    expectedProgress: (React.createElement(Tooltip, { id: "tooltip-route-details" },
        React.createElement(Translation, null, (t) => t('EXPECTED_PROGRESS_TOOLTIP')))),
    actualProgress: (React.createElement(Tooltip, { id: "tooltip-route-details" },
        React.createElement(Translation, null, (t) => t('ACTUAL_PROGRESS_TOOLTIP')))),
    performance: (React.createElement(Tooltip, { id: "tooltip-route-details" },
        React.createElement(Translation, null, (t) => t('PERFORMANCE_TOOLTIP')))),
    deleteCarrier: (React.createElement(Tooltip, { id: "tooltip-remove-carrier" },
        React.createElement(Translation, null, (t) => t('CLICK_TO_REMOVE_CARRIER')))),
};
const getTimezone = (zone) => {
    return moment.tz(zone).zoneAbbr();
};
const formatDate = (date, timezone) => {
    const DATE_FORMAT = 'L LT';
    const formattedDate = moment(date).tz(timezone).format(DATE_FORMAT);
    const formattedTimezone = getTimezone(timezone) || '';
    return `${formattedDate} ${formattedTimezone}`;
};
const getDirectionClassName = (direction) => {
    if (direction === 'up') {
        return 'fa fa-arrow-up';
    }
    return 'fa fa-arrow-down';
};
const getAppointmentLabel = (appointment) => {
    const { date, timezone, appointment_unconfirmed } = appointment;
    if (date && !appointment_unconfirmed) {
        return formatDate(date, timezone);
    }
    return React.createElement(Translation, null, (t) => t('NEEDS_APPOINTMENT'));
};
const StopSummary = ({ label, iconDirection, address, date, value, timezone, appointment_unconfirmed, }) => (React.createElement("div", { "data-testid": "div-4c77ba51", className: "stop__address" },
    React.createElement("p", { "data-testid": "p-76340254" },
        React.createElement("strong", { "data-testid": "strong-349caf11" }, label),
        value),
    React.createElement("p", { "data-testid": "p-f56ec6d0" },
        React.createElement("i", { "data-testid": "i-521a0667", className: getDirectionClassName(iconDirection) }),
        ' ',
        address),
    React.createElement("p", { "data-testid": "p-6d91f927" },
        React.createElement("small", { "data-testid": "small-1110e447" },
            React.createElement("i", { "data-testid": "i-0419a98e", className: "fa fa-clock-o" }),
            getAppointmentLabel({
                date,
                timezone,
                appointment_unconfirmed,
            })))));
StopSummary.propTypes = {
    label: PropTypes.string.isRequired,
    iconDirection: PropTypes.oneOf(['up', 'down']),
    address: PropTypes.string.isRequired,
    date: PropTypes.string,
    value: PropTypes.string,
    timezone: PropTypes.string,
    appointment_unconfirmed: PropTypes.bool,
};
function getStopSummaryInfo(stop) {
    const { window_start, window_end, appointment_unconfirmed } = stop;
    const locations = stop.location || [];
    const location = locations[0] || {};
    const timezone = getLocationTimezone(location);
    return {
        location,
        window_start,
        window_end,
        timezone,
        appointment_unconfirmed,
    };
}
function isShipmentWithHazards(shipment) {
    var _a;
    const shipmentHasHazmatDescription = shipment.hasOwnProperty('hazmat_description') &&
        shipment.hazmat_description;
    if (isIntermodalShipmentType(shipment)) {
        const order = (_a = shipment === null || shipment === void 0 ? void 0 : shipment.order) !== null && _a !== void 0 ? _a : {};
        return isOrderHazmat(order);
    }
    return shipmentHasHazmatDescription;
}
class StatusBar extends Component {
    constructor(props) {
        super(props);
        this.handleSaveToggleExposed = (exposed) => {
            this.setState({ exposed, showConfirmToggleExposed: true });
        };
        this.handleConfirmToggleExposed = () => {
            this.setState({ showConfirmToggleExposed: false }, () => {
                this.props.onSaveToggleExposed(this.props.shipment, this.state.exposed);
            });
        };
        this.handleHideToggleExposed = () => this.setState({ showConfirmToggleExposed: false });
        this.handleSelection = (key) => () => {
            switch (key) {
                case 'assign':
                    this.props.onAssignDriver();
                    break;
                case 'email_delivery_order':
                    this.props.onSendDOEMailModal();
                    break;
                case 'accessorials':
                    this.props.onModifyAccessorials();
                    break;
                case 'bol':
                    this.props.onSendBOLMailModal();
                    break;
                case 'rate':
                    this.props.onSendRATEEmailModal();
                    break;
                case 'create_hold':
                    this.props.onCreateHold();
                    break;
                case 'open':
                    this.props.onReOpenShipment();
                    break;
                case 'cancel':
                    this.props.onShowCancelShipmentModal();
                    break;
                case 'clone':
                    this.props.onCloneShipment();
                    break;
                case 'bulk_accessorials':
                    this.props.onBulkAddAccessorial();
                    break;
                case 'generate_carrier_204_tendered':
                    this.props.onGenerateCarrier204('load_tendered');
                    break;
                case 'generate_carrier_204_updated':
                    this.props.onGenerateCarrier204('load_updated');
                    break;
                case 'generate_carrier_204_canceled':
                    this.props.onGenerateCarrier204('load_canceled');
                    break;
                case 'overrideBillingStatus':
                    this.props.onOverrideBillingStatus();
                    break;
                case 'markBillingStatusPendingReview':
                    this.props.onMarkBillingStatusPendingReview();
                    break;
                default:
            }
        };
        this.renderRemoveCarrierButton = () => {
            if (!isIntermodalShipmentType(this.props.shipment))
                return null;
            return (React.createElement("button", { "data-testid": "button-27e2e87a", onClick: this.props.onRemoveCarrier, "aria-label": "remove button" },
                React.createElement("span", { "data-testid": "span-a8bab95c", className: "text-sm text-red-500" },
                    React.createElement(OverlayTrigger, { placement: "right", overlay: TOOLTIPS.deleteCarrier },
                        React.createElement(Glyphicon, { glyph: "remove" })))));
        };
        this.transition = (status) => {
            this.props.onShipmentTransition(this.props.shipment, status, {});
        };
        this.state = {
            showConfirmToggleExposed: false,
            exposed: false,
        };
    }
    canManageAccessorials() {
        const shipment = this.props.shipment;
        const isFinanceUser = SessionStore.getUserRoles().indexOf('finance') > -1;
        if (isIntermodalShipmentType(shipment) && !shipment.carrier) {
            return false;
        }
        if (isFinanceUser) {
            return true;
        }
        if (isIntermodalShipmentType(shipment.shipment_type) && !shipment.current) {
            return false;
        }
        if (!isIntermodalShipmentType(shipment) &&
            (shipment.invoice || shipment.payout)) {
            return false;
        }
        return true;
    }
    canOpenJob() {
        const shipment = this.props.shipment;
        if (shipment.accepted && shipment.current)
            return true;
        // The next two are for shorthaul, since invoice and payout (singular) don't exist on drayage shipments.
        if (!shipment.current && shipment.invoice)
            return false;
        if (!shipment.current && shipment.payout)
            return false;
        if (shipment.current)
            return false;
        return true;
    }
    canDriverAssign() {
        const shipment = this.props.shipment;
        const { status, shipment_type, accepted, current, canceled, payout } = shipment;
        if (checkShorthaulAppointmentNeeded(shipment_type, status))
            return false;
        if (!accepted)
            return true;
        if (accepted && current)
            return true;
        if (!current)
            return false;
        if (!canceled)
            return false;
        return !payout;
    }
    getStatus() {
        const { t } = this.props;
        const status = this.props.shipment.status;
        switch (status) {
            case 'job_closed':
                return t('CLOSED');
            case 'job_canceled':
                return t('CANCELED');
            case 'needs_appt':
                return t('NEEDS_APPT');
            default:
        }
        return t(sluggify(status));
    }
    getQuickPayTag() {
        var _a;
        const { t } = this.props;
        const quick_payment_contract = (_a = this.props.shipment) === null || _a === void 0 ? void 0 : _a.quick_payment_contract;
        if (quick_payment_contract)
            return (React.createElement(Badge, { className: "ml-2", color: "blue" }, t('QUICK_PAY')));
    }
    canClone() {
        const { shipment } = this.props;
        if (isIntermodalShipmentType(shipment) || shipment.order) {
            return false;
        }
        if (shipment && shipment.shipper && shipment.shipper.tags) {
            const tags = shipment.shipper.tags;
            if (tags.indexOf('block_shipment_clones') > -1) {
                return false;
            }
        }
        return true;
    }
    renderCarrierPopover() {
        const { shipment } = this.props;
        return (React.createElement(Popover, { id: "carrier-popover" },
            React.createElement("h4", { "data-testid": "h4-8ebe970f" }, unescapeHTML(this.props.shipment.carrier_name)),
            React.createElement("p", { "data-testid": "p-72e0ebb1" },
                React.createElement("i", { "data-testid": "i-b8996879", className: "fa fa-phone", style: { marginRight: '0.5rem' } }),
                shipment.carrier.phone)));
    }
    renderCarrier() {
        var _a;
        if (!this.props.shipment.carrier) {
            return null;
        }
        const { t, shipment: { carrier }, } = this.props;
        const isPortProCarrier = 'portpro' in carrier && ((_a = carrier === null || carrier === void 0 ? void 0 : carrier.portpro) === null || _a === void 0 ? void 0 : _a.enabled);
        return (React.createElement(DescriptionListItem, { dataTestId: "DescriptionListItem-7132f3fd", label: titleize(t('CARRIER', { count: 1 })), className: "" },
            React.createElement("div", { "data-testid": "div-5a585ab5", className: "flex items-center space-x-2" },
                React.createElement("div", { "data-testid": "div-525c15e5", className: "flex items-center space-x-1" },
                    React.createElement("div", { "data-testid": "div-c42e88ca", className: "relative" },
                        React.createElement(Link, { to: `/users/${carrier._id}` },
                            React.createElement("strong", { "data-testid": "strong-66dfa6de" }, unescapeHTML(this.props.shipment.carrier_name))),
                        carrier.mvp ? (React.createElement("img", { "data-testid": "img-d7503e76", src: "/img/cargomatic-circle.png", className: "absolute -top-5 -right-2 w-3", alt: "MVP" })) : null,
                        isPortProCarrier ? (React.createElement("img", { "data-testid": "img-824e88df", src: "/img/icons/portProLogo.png", className: "absolute -top-6 -right-11", title: t('CARRIER_INTEGRATED_WITH_PORTPRO'), alt: t('CARRIER_INTEGRATED_WITH_PORTPRO') })) : null),
                    React.createElement(OverlayTrigger, { trigger: "click", placement: "bottom", overlay: this.renderCarrierPopover() },
                        React.createElement("div", { "data-testid": "div-be76024e" },
                            React.createElement(InfoIcon, { className: "w-4 h-4 cursor-pointer" })))),
                this.renderRemoveCarrierButton())));
    }
    renderShipperPopover() {
        const { shipment: { shipper }, } = this.props;
        return (React.createElement(Popover, { id: "shipper-popover" },
            React.createElement("h4", { "data-testid": "h4-42879a61" }, unescapeHTML(shipper.company_name)),
            React.createElement("h5", { "data-testid": "h5-45c05096" },
                React.createElement(Trans, { i18nKey: "SALES_REP_WITH_NAME", values: {
                        sales_rep: shipper.sales_rep,
                    }, components: [
                        React.createElement("strong", { key: "sales-rep-strong", "data-testid": "strong-063f8aa5", style: { marginRight: '0.5rem' } }),
                    ] })),
            React.createElement("p", { "data-testid": "p-4c513b5c" },
                React.createElement("i", { "data-testid": "i-e0cf7264", className: "fa fa-phone", style: { marginRight: '0.5rem' } }),
                shipper.phone),
            React.createElement("p", { "data-testid": "p-bcaaaa17" },
                React.createElement("i", { "data-testid": "i-bd86dde5", className: "fa fa-envelope", style: { marginRight: '0.5rem' } }),
                shipper.email)));
    }
    renderShipper() {
        const { t } = this.props;
        return (React.createElement(DescriptionListItem, { dataTestId: "DescriptionListItem-e8764ae4", label: titleize(t('SHIPPER', { count: 1 })), className: "" },
            React.createElement("div", { "data-testid": "div-a66d80f4", className: "flex items-center space-x-2" },
                React.createElement("div", { "data-testid": "div-6b5bd0f2", className: "flex items-center space-x-1" },
                    React.createElement(Link, { to: `/users/${this.props.shipment.shipper._id}` },
                        React.createElement("strong", { "data-testid": "strong-22102e64" }, unescapeHTML(this.props.shipment.shipper_name))),
                    React.createElement(OverlayTrigger, { trigger: "click", placement: "bottom", overlay: this.renderShipperPopover() },
                        React.createElement("div", { "data-testid": "div-3aa4d126" },
                            React.createElement(InfoIcon, { className: "w-4 h-4 cursor-pointer" })))))));
    }
    renderConsigneePopover() {
        const { shipment: { consignee }, } = this.props;
        return (React.createElement(Popover, { id: "consignee-popover" },
            React.createElement("h4", { "data-testid": "h4-98629b40" }, unescapeHTML(consignee === null || consignee === void 0 ? void 0 : consignee.company_name)),
            React.createElement("p", { "data-testid": "p-839f07cd" },
                React.createElement("i", { "data-testid": "i-2b933f5a", className: "fa fa-phone", style: { marginRight: '0.5rem' } }), consignee === null || consignee === void 0 ? void 0 :
                consignee.phone),
            React.createElement("p", { "data-testid": "p-d3cc157c" },
                React.createElement("i", { "data-testid": "i-3ee5a903", className: "fa fa-envelope", style: { marginRight: '0.5rem' } }), consignee === null || consignee === void 0 ? void 0 :
                consignee.email)));
    }
    renderConsignee() {
        var _a, _b, _c, _d, _e, _f;
        if (!((_a = this.props.shipment) === null || _a === void 0 ? void 0 : _a.consignee)) {
            return null;
        }
        const { t } = this.props;
        return (React.createElement(DescriptionListItem, { dataTestId: "DescriptionListItem-df4119c2", label: titleize(t('CONSIGNEE', { count: 1 })), className: "" },
            React.createElement("div", { "data-testid": "div-2e11febc", className: "flex items-center space-x-2" },
                React.createElement("div", { "data-testid": "div-548e9d1f", className: "flex items-center space-x-1" },
                    React.createElement(Link, { to: `/users/${(_d = (_c = (_b = this.props) === null || _b === void 0 ? void 0 : _b.shipment) === null || _c === void 0 ? void 0 : _c.consignee) === null || _d === void 0 ? void 0 : _d._id}` },
                        React.createElement("strong", { "data-testid": "strong-2c0785c7" }, unescapeHTML((_f = (_e = this.props) === null || _e === void 0 ? void 0 : _e.shipment) === null || _f === void 0 ? void 0 : _f.consignee_name))),
                    React.createElement(OverlayTrigger, { trigger: "click", placement: "bottom", overlay: this.renderConsigneePopover() },
                        React.createElement("div", { "data-testid": "div-4f41697d" },
                            React.createElement(InfoIcon, { className: "w-4 h-4 cursor-pointer" })))))));
    }
    renderDriverPopover() {
        const { shipment } = this.props;
        return (React.createElement(Popover, { id: "driver-popover" },
            React.createElement("h4", { "data-testid": "h4-f185e004" }, unescapeHTML(shipment.driver_name)),
            React.createElement("p", { "data-testid": "p-be5ecd9d" },
                React.createElement("i", { "data-testid": "i-5e352470", className: "fa fa-phone", style: { marginRight: '0.5rem' } }),
                shipment.driver.phone)));
    }
    renderDriver() {
        if (!this.props.shipment.driver)
            return null;
        const { t } = this.props;
        return (React.createElement(DescriptionListItem, { dataTestId: "DescriptionListItem-8043a6a8", className: "", label: titleize(t('DRIVER', { count: 1 })) },
            React.createElement("div", { "data-testid": "div-cf245287", className: "flex items-center space-x-2" },
                React.createElement("div", { "data-testid": "div-863fdc99", className: "flex items-center space-x-1" },
                    React.createElement(Link, { to: `/users/${this.props.shipment.driver._id}` },
                        React.createElement("strong", { "data-testid": "strong-42e5d6f6" }, unescapeHTML(this.props.shipment.driver_name))),
                    React.createElement(OverlayTrigger, { placement: "bottom", trigger: "click", overlay: this.renderDriverPopover() },
                        React.createElement("div", { "data-testid": "div-e5a952d5" },
                            React.createElement(InfoIcon, { className: "w-4 h-4 cursor-pointer" })))),
                this.renderRemoveCarrierButton())));
    }
    renderReviewDate() {
        const { t, shipment } = this.props;
        return (React.createElement(ShipmentDetailDateItem, { dataTestId: "shipmentStatusBarReviewDate", className: "mt-2 lg:col-span-2 z-[45]", titleClassName: "!mb-0 text-sm font-medium text-gray-500", title: t('REVIEW_DATE'), key: "Review Date", heapClassName: "shipment-shipmentDetails-shipmentStatusBarReviewDate heap", value: shipment.review_date, editable: true, onSave: (event, value) => {
                ShipmentsActionCreators.updateImportantDates(shipment, event, value);
            }, param: "review_date", useNamedUpdate: true }));
    }
    renderFirstStop() {
        const { t } = this.props;
        const firstStop = getFirstStop(this.props.shipment.stops);
        if (!firstStop) {
            return null;
        }
        const stopInfo = getStopSummaryInfo(firstStop);
        return (React.createElement(StopSummary, { label: `${t('STARTING_ORIGIN')} `, value: humanize(unescapeHTML(stopInfo.location.name)), address: stopInfo.location.address, iconDirection: "up", date: stopInfo.window_end, timezone: stopInfo.timezone, appointment_unconfirmed: stopInfo.appointment_unconfirmed }));
    }
    renderLastStop() {
        const { t } = this.props;
        const lastStop = getLastStop(this.props.shipment.stops);
        if (!lastStop) {
            return null;
        }
        const stopInfo = getStopSummaryInfo(lastStop);
        return (React.createElement(StopSummary, { label: `${t('FINISING_DESTINATION')} `, value: humanize(unescapeHTML(stopInfo.location.name)), address: stopInfo.location.address, iconDirection: "down", date: stopInfo.window_end, timezone: stopInfo.timezone, appointment_unconfirmed: stopInfo.appointment_unconfirmed }));
    }
    renderStateTransitions() {
        return (React.createElement("div", { "data-testid": "div-aa55e577", className: "" },
            React.createElement(StateTransition, { dataTestId: "StateTransition-9cefc01b", progress: this.props.progress ? this.props.progress : null, regress: this.props.regress ? this.props.regress : null, onClick: this.transition })));
    }
    renderProgress() {
        const { shipment: { expected_progress_percentage, actual_progress_percentage, total_expected_progress_percentage, performance_percentage, }, t, } = this.props;
        return (React.createElement("div", { "data-testid": "div-dfd7845f" },
            React.createElement("strong", { "data-testid": "strong-f9c8c16e" },
                t('EXPECTED_PROGRESS'),
                " "),
            React.createElement(OverlayTrigger, { placement: "top", overlay: TOOLTIPS.expectedProgress },
                React.createElement(Glyphicon, { glyph: "info-sign" })),
            React.createElement(ProgressCell, { actual: expected_progress_percentage, expected: total_expected_progress_percentage }),
            React.createElement("strong", { "data-testid": "strong-67624d13" },
                t('ACTUAL_PROGRESS'),
                " "),
            React.createElement(OverlayTrigger, { placement: "top", overlay: TOOLTIPS.actualProgress },
                React.createElement(Glyphicon, { glyph: "info-sign" })),
            React.createElement(ProgressCell, { actual: actual_progress_percentage, expected: total_expected_progress_percentage }),
            React.createElement("strong", { "data-testid": "strong-450a928d" },
                t('PERFORMANCE'),
                " "),
            React.createElement(OverlayTrigger, { placement: "top", overlay: TOOLTIPS.performance },
                React.createElement(Glyphicon, { glyph: "info-sign" })),
            React.createElement(ProgressCell, { actual: performance_percentage, expected: 100 })));
    }
    renderActionsMenu() {
        let { shipment, t } = this.props;
        const { status, shipment_type } = shipment;
        const shouldDisableBOL = DISABLED_BOL_STATUSES.includes(status) || isShipmentWithHazards(shipment);
        const shouldShow = shipment_type === shipmentTypes.SHORTHAUL ||
            shipment_type === shipmentTypes.LONGHAUL ||
            shipment_type === shipmentTypes.DAYRATE;
        const shouldShowRATE = !shouldShow;
        const shouldDisableAccessorial = isIntermodalShipmentType(shipment) && status === 'job_canceled'
            ? isShipmentCanceled(shipment)
            : !this.canManageAccessorials();
        return (React.createElement(DropdownMenu, { dataTestId: "DropdownMenu-8e367603", title: t('ACTIONS') },
            React.createElement(Menu.Item, { "data-testid": "undefined-485b8e0a", id: "manage_assign_menu", disabled: !this.canDriverAssign(), key: "assign" },
                React.createElement(DropdownItem, { dataTestId: "DropdownItem-8bd24833", onClick: this.handleSelection('assign'), disabled: !this.canDriverAssign() }, t('ASSIGN'))),
            isIntermodalShipmentType(this.props.shipment) && (React.createElement(Menu.Item, { "data-testid": "undefined-7236c8f6", key: "email_delivery_order" },
                React.createElement(DropdownItem, { dataTestId: "DropdownItem-31b32d5e", onClick: this.handleSelection('email_delivery_order'), disabled: false }, t('EMAIL_DELIVERY_ORDER')))),
            React.createElement(Menu.Item, { "data-testid": "undefined-5d519bf3", key: "open" },
                React.createElement(DropdownItem, { dataTestId: "DropdownItem-079ec89a", disabled: !this.canOpenJob(), onClick: this.handleSelection('open') }, t('REOPEN'))),
            React.createElement(Menu.Item, { "data-testid": "undefined-369494dd", key: "bol" },
                React.createElement(DropdownItem, { dataTestId: "DropdownItem-12f9eb03", disabled: shouldDisableBOL, onClick: this.handleSelection('bol') }, t('GENERATE_BOL'))),
            React.createElement(Menu.Item, { "data-testid": "undefined-0294101b", key: "tracking_link" },
                React.createElement(DropdownItem, { dataTestId: "DropdownItem-ec6da4e1", href: `/tracking?id=${this.props.shipment.shipment_id}`, target: "_blank" }, t('TRACKING_LINK'))),
            React.createElement(Menu.Item, { "data-testid": "undefined-37c2fc33", key: "create_hold" },
                React.createElement(DropdownItem, { dataTestId: "DropdownItem-4f667d81", onClick: this.handleSelection('create_hold') }, t('CREATE_HOLD'))),
            !shouldShowRATE && (React.createElement(Menu.Item, { "data-testid": "undefined-1b6e77ba", key: "rate" },
                React.createElement(DropdownItem, { dataTestId: "DropdownItem-beeaa674", onClick: this.handleSelection('rate') }, t('GENERATE_RATE_CONFIRMATION')))),
            !isProd() && (React.createElement(Menu.Item, { "data-testid": "undefined-8a79dd20", key: "generate_carrier_204_tendered" },
                React.createElement(DropdownItem, { dataTestId: "DropdownItem-cd970264", onClick: this.handleSelection('generate_carrier_204_tendered') }, t('SEND_EDI204_TENDERED')))),
            !isProd() && (React.createElement(Menu.Item, { "data-testid": "undefined-fe2e7f9d", key: "generate_carrier_204_updated" },
                React.createElement(DropdownItem, { dataTestId: "DropdownItem-3aaa479d", onClick: this.handleSelection('generate_carrier_204_updated') }, t('SEND_EDI204_UPDATED')))),
            !isProd() && (React.createElement(Menu.Item, { "data-testid": "undefined-c59e7a8f", key: "generate_carrier_204_canceled" },
                React.createElement(DropdownItem, { dataTestId: "DropdownItem-9636c58d", onClick: this.handleSelection('generate_carrier_204_canceled') }, t('SEND_EDI204_CANCELED')))),
            React.createElement(Menu.Item, { "data-testid": "undefined-7c9050da", key: "accessorials" },
                React.createElement(DropdownItem, { dataTestId: "DropdownItem-bf52af0f", disabled: shouldDisableAccessorial, onClick: this.handleSelection('accessorials') }, t('ACCESSORIAL', { count: 3 }))),
            isIntermodalShipmentType(this.props.shipment) && (React.createElement(Menu.Item, { "data-testid": "undefined-eb32eb1a", key: "bulk_accessorials" },
                React.createElement(DropdownItem, { dataTestId: "DropdownItem-961d75b8", href: "#", disabled: shouldDisableAccessorial, onClick: this.handleSelection('bulk_accessorials') }, t('BULK_ACCESSORIALS')))),
            React.createElement(Menu.Item, { "data-testid": "undefined-d8131ab0", key: "clone" },
                React.createElement(DropdownItem, { dataTestId: "DropdownItem-ef746353", disabled: !this.canClone(), onClick: this.handleSelection('clone') }, t('CLONE'))),
            React.createElement(Menu.Item, { "data-testid": "undefined-3be5c5be", key: "cancel" },
                React.createElement(DropdownItem, { dataTestId: "DropdownItem-b2d8cbe7", disabled: !this.props.shipment.current, onClick: this.handleSelection('cancel') }, t('CANCEL'))),
            SessionStore.isBillingStatusAdmin() && (React.createElement(Menu.Item, { key: "overrideBillingStatus" },
                React.createElement(DropdownItem, { onClick: this.handleSelection('overrideBillingStatus') }, t('OVERRIDE_BILLING_STATUS'))))));
    }
    renderHazmatAlert() {
        var _a;
        const { t, shipment } = this.props;
        const order = (_a = shipment === null || shipment === void 0 ? void 0 : shipment.order) !== null && _a !== void 0 ? _a : {};
        const hasHazmat = isOrderHazmat(order);
        if (!hasHazmat) {
            return null;
        }
        return (React.createElement(Alert, { dataTestId: "Alert-05b61aey", type: "warning", className: "mb-4", title: t('HAZMAT_WARNING_HEADER') },
            React.createElement("p", { "data-testid": "p-7ddcba2f" }, t('HAZMAT_WARNING_BODY'))));
    }
    renderPreNoticeAlert() {
        const { t, shipment } = this.props;
        const { pre_arrival = false } = shipment || {};
        if (!pre_arrival ||
            !featureEnabled(CONFIGURED_FEATURES.AMAZON_PRE_ARRIVAL)) {
            return null;
        }
        return (React.createElement(Alert, { dataTestId: "Alert-07bf1a4d", type: "error", className: "mb-4", title: t('PRE_NOTICE_SHIPMENT_HEADER') },
            React.createElement("p", { "data-testid": "p-7ddcba2f" }, t('PRE_NOTICE_SHIPMENT_BODY'))));
    }
    renderCarrierRejectionAlert() {
        var _a, _b, _c;
        const { t, shipment } = this.props;
        const hasCarrierRejectedShipment = (_c = (_b = (_a = shipment.carrier) === null || _a === void 0 ? void 0 : _a.portpro) === null || _b === void 0 ? void 0 : _b.rejected) === null || _c === void 0 ? void 0 : _c.includes(shipment._id);
        if (!hasCarrierRejectedShipment) {
            return null;
        }
        return (React.createElement(Alert, { dataTestId: "Alert-8852c662", type: "warning", className: "mb-4", title: t('REJECTION_WARNING_HEADER') },
            React.createElement("p", { "data-testid": "p-ea0c6bf1" }, t('REJECTION_WARNING_BODY'))));
    }
    render() {
        var _a, _b, _c, _d;
        const { shipment: { status, stops, shipment_id, shipment_type, payables, on_hold, carrier, }, onNextActionsDateClick, t, } = this.props;
        const isDrayageOrRailShipment = shipment_type &&
            (shipment_type === 'drayage' || shipment_type === 'rail');
        let exposeHidden = true;
        const carrierCost = payables
            .filter((c) => c.message && c.message.includes('Carrier Cost'))
            .reduce((sum, c) => sum + (c.value || 0), 0);
        const hasAutoExposeDrayageShipmentTag = (((_b = (_a = this.props.shipment) === null || _a === void 0 ? void 0 : _a.shipper) === null || _b === void 0 ? void 0 : _b.tags) || []).includes('auto_expose_drayage_shipments');
        if (isDrayageOrRailShipment && !carrier) {
            exposeHidden = false;
        }
        if (!stops) {
            return null;
        }
        return (React.createElement("div", { "data-testid": "div-f81b29f7", className: "relative px-4 py-5 mb-4 bg-white rounded-t-lg shadow lg:grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 sm:p-6" },
            React.createElement("div", { "data-testid": "div-a00cfcb4", className: "lg:col-span-12" },
                this.renderPreNoticeAlert(),
                this.renderHazmatAlert(),
                this.renderCarrierRejectionAlert(),
                React.createElement(OpenExceptions, { shipment: this.props.shipment }),
                React.createElement("div", { "data-testid": "div-2f90417a", className: "flex items-center justify-between flex-wrap" },
                    React.createElement("div", { "data-testid": "div-0d8b52c9", className: "flex items-center flex-wrap gap-8" },
                        React.createElement("div", { className: "flex items-center gap-2" },
                            React.createElement("h3", { "data-testid": "h3-4e21e032", className: "my-0" }, shipment_id),
                            React.createElement(Badge, { dataTestId: "Badge-41e328b1", className: "", color: getBadgeColor(status) }, this.getStatus()),
                            React.createElement(BillingStatusRenderer, Object.assign({}, this.props)),
                            on_hold && (React.createElement(Badge, { dataTestId: "Badge-a8f30095", className: "", color: "red" }, t('ON_HOLD'))),
                            this.getQuickPayTag()),
                        isIntermodalShipmentType(this.props.shipment) && (React.createElement("div", { className: "mt-4 lg:mt-0" },
                            React.createElement(LegNumberSummary, { id: (_d = (_c = this.props) === null || _c === void 0 ? void 0 : _c.shipment) === null || _d === void 0 ? void 0 : _d._id })))),
                    React.createElement("div", { "data-testid": "div-c4cdb935", className: "flex flex-wrap items-center my-4 lg:my-0 gap-4" },
                        !exposeHidden && (React.createElement("div", { "data-testid": "div-881f5657", className: "flex items-center" },
                            React.createElement("label", { "data-testid": "label-8b29d2bf", className: "mb-0 mr-2 form-label" }, t('EXPOSE')),
                            React.createElement(Switch, { className: "mb-0", checked: this.props.shipment.exposed, onChange: this.handleSaveToggleExposed, offColor: "#c53030" }))),
                        this.renderStateTransitions(),
                        React.createElement("div", { "data-testid": "div-82424d60", className: "" }, this.renderActionsMenu()),
                        this.props.showCloseButton && this.props.onClose && (React.createElement("div", { "data-testid": "div-ccbd4672", className: "absolute top-0 right-0" },
                            React.createElement(Button, { size: "icon", variant: "icon", "data-testid": "button-7904e57e", className: "w-10 h-10", onClick: () => this.props.onClose(), "aria-label": "close button" },
                                React.createElement(XIcon, null))))))),
            React.createElement("div", { "data-testid": "div-56f513c8", className: "lg:col-span-4" },
                React.createElement("div", { "data-testid": "div-8cadbdeb", className: "lg:grid lg:grid-cols-2 lg:gap-2" },
                    this.renderShipper(),
                    this.renderCarrier(),
                    this.renderDriver(),
                    this.renderConsignee(),
                    this.renderReviewDate())),
            featureEnabled(CONFIGURED_FEATURES.SSR) &&
                isIntermodalShipmentType(this.props.shipment) ? (React.createElement(NextSteps, { onNextActionsDateClick: (item) => onNextActionsDateClick(item), shipment: this.props.shipment })) : (React.createElement(React.Fragment, null,
                React.createElement("div", { "data-testid": "div-d6592829", className: "lg:col-span-3" }, this.renderFirstStop()),
                React.createElement("div", { "data-testid": "div-8548b554", className: "lg:col-span-3" }, this.renderLastStop()),
                React.createElement("div", { "data-testid": "div-9ff9482f", className: "lg:col-span-2" }, this.renderProgress()))),
            featureEnabled(CONFIGURED_FEATURES.SSR) &&
                isIntermodalShipmentType(this.props.shipment) && (React.createElement(MinimumDocumentRequirements, { shipment: this.props.shipment })),
            this.state.showConfirmToggleExposed && (React.createElement(ConfirmModal, { dataTestId: "ConfirmModal-b5242d33", title: this.state.exposed ? t('EXPOSE_SHIPMENT') : t('UNEXPOSE_SHIPMENT'), message: !hasAutoExposeDrayageShipmentTag && carrierCost <= 0
                    ? t('WARNING_CARRIER_COST_ARE_YOU_SURE')
                    : t('ARE_YOU_SURE'), show: this.state.showConfirmToggleExposed, onConfirm: this.handleConfirmToggleExposed, onHide: this.handleHideToggleExposed }))));
    }
}
StatusBar.propTypes = {
    shipment: PropTypes.shape({
        shipment_id: PropTypes.string.isRequired,
        shipper_name: PropTypes.string,
        carrier_name: PropTypes.string,
        driver_name: PropTypes.string,
        carrier: PropTypes.shape({
            _id: PropTypes.string,
            email: PropTypes.string,
            phone: PropTypes.string,
        }),
        driver: PropTypes.shape({
            _id: PropTypes.string,
            email: PropTypes.string,
            phone: PropTypes.string,
        }),
        shipper: PropTypes.shape({
            _id: PropTypes.string,
            email: PropTypes.string,
            phone: PropTypes.string,
            sales_rep: PropTypes.string,
        }),
        stops: PropTypes.arrayOf(PropTypes.shape({
            location: PropTypes.arrayOf(PropTypes.shape({
                name: PropTypes.string.isRequired,
                address: PropTypes.string.isRequired,
            })),
        })).isRequired,
        on_hold: PropTypes.bool.isRequired,
        pre_arrival: PropTypes.bool,
    }),
    onRemoveCarrier: PropTypes.func,
};
export default withTranslation()(StatusBar);
